// import { Div, ColumnDiv, ScrollableContainerInner, ScrollableContainerOuter } from 'styles/elements/div';
/* istanbul ignore file */
import { Modal as MuiModal } from '@econsult/econsult-ui/dist/components/Modal';
import { Paragraph } from '@econsult/econsult-ui/dist/components/shared/typography';
import styled from 'styled-components';
import { Button } from '@econsult/econsult-ui/dist/components/Button';
import { InvisibleButton } from 'styles/elements/buttons';
import { rgba } from 'polished';
import { CloseIcon } from '@econsult/econsult-ui/dist/assets';
import { FlexColumnDiv, FlexDiv, ScrollableContainerOuter, ScrollableContainerInner } from 'components/Div/styles';

export const ModalContainer = styled(FlexColumnDiv)`
  display: flex;
  background: rgb(248, 246, 247);
  border-radius: 6px;
  width: 50%;
  height: 85%;
  margin-top: -48px;
  overflow-y: auto;
`;

export const ModalTitleAndCloseContainer = styled(FlexDiv)`
  background-color: ${({ theme }) => theme.bgModalPatientInfo};
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  padding: 24px 40px 4px;
  justify-content: space-between;
  align-items: center;
`;

export const ModalTitle = styled(Paragraph).attrs({ typography: 'ParagraphBaseHeavy' })`
  ${({ theme }) => theme.fontStyles};
  color: ${({ theme }) => theme.colorPrimaryPrimary100};
  font-weight: ${({ theme }) => theme.fontWeight600};
  margin: 0;
`;

export const ModalContentsOuter = styled(FlexColumnDiv)`
  display: flex;
  margin-top: 16px;
`;

interface ModalContentsInnerProps {
  isNewMessage?: boolean;
}

export const ModalContentsInner = styled(FlexColumnDiv)<ModalContentsInnerProps>`
  display: flex;
  flex: 1 1 auto;
  margin: 0 40px 20px 40px;
  padding: ${({ isNewMessage }) => (isNewMessage ? 0 : '24px')};
  border-radius: 6px;
  border: ${({ theme }) => theme.matchingContainerBorder};
  background: ${({ theme, isNewMessage }) => (isNewMessage ? `${theme.colorNeutralBrightWhite}` : `${theme.bgModalPatientInfo}`)};
  font-weight: ${({ theme }) => theme.fontWeight600};
  font-size: ${({ theme }) => theme.fontSizeMedium};
  color: ${({ theme }) => theme.dropdownPrimary};
`;

export const ModalCloseButton = styled(InvisibleButton)`
  color: ${({ theme }) => theme.wrapperModalCloseButton};
  border: 2px solid transparent;
  outline: none;
  padding: 0;
  &:focus {
    border: 2px solid ${({ theme }) => theme.wrapperModalCloseButton};
  }
  &:active {
    outline: none;
  }
`;

export const ModalCloseIcon = styled(CloseIcon)`
  margin: 0;
  padding: 0;
`;

export const ModalScrollableContainerOuter = styled(ScrollableContainerOuter)`
  flex: 1 1 auto;
  margin-bottom: 1px;
  overflow: visible;
`;

export const ModalScrollableContainerInner = styled(ScrollableContainerInner)`
  position: relative;
`;

export const ModalConfirmButton = styled(Button).attrs({ size: 'small' })`
  cursor: pointer;
  border-radius: 6px;
`;

export const ModalCancelButton = styled(ModalConfirmButton).attrs({ state: 'secondary' })``;

export const ModalActionsContainer = styled(FlexDiv)`
  justify-content: center;
  gap: 8px;
  padding-bottom: 24px;
`;

export const ButtonWrapper = styled.div`
  display: flex;
  justify-content: end;
  gap: 1rem;
`;

export const RevertButton = styled(Button).attrs({ size: 'small' })`
  && {
    color: ${({ theme }) => theme.colorActionPrimaryDefault};
    background: ${({ theme }) => theme.colorPrimaryPrimary20};
    border-color: ${({ theme }) => theme.colorPrimaryPrimary20};
    cursor: pointer;
  }
`;

export const ConfirmButton = styled(Button).attrs({ size: 'small' })`
  && {
    background: ${({ theme }) => theme.colorStatusError};
    border-color: ${({ theme }) => theme.colorStatusError};
    cursor: pointer;

    &&[disabled] {
      background-color: ${({ theme }) => rgba(theme.colorStatusError, 0.1)} !important;
      color: ${({ theme }) => rgba(theme.colorStatusError, 0.5)} !important;
      border-color: transparent !important;
    }
  }
`;

export const TemplateManagerSideBar = styled.section`
  display: grid;
  grid-template-rows: auto auto 1fr auto;
  background: ${({ theme }) => theme.colorNeutralNeutral9};
`;

export const TemplateManagerMain = styled.section`
  display: grid;
  grid-template-rows: 1fr auto;
  background: ${({ theme }) => theme.colorNeutralBrightWhite};
  border-left: 1px solid ${({ theme }) => theme.colorNeutralNeutral8};
`;

export const SidebarEmptyGraphic = styled(FlexColumnDiv)`
  height: 100%;
  gap: 1rem;
  align-items: center;
  justify-content: center;
`;

export const SidebarList = styled.ul`
  margin: 0;
  padding: 0.25rem 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  overflow-y: scroll;
  scrollbar-gutter: stable;
  list-style: none;
  background: ${({ theme }) => theme.colorBackgroundSoftWhite};
`;

export const SidebarListHeading = styled(Paragraph).attrs({ typography: 'ParagraphTinyRegular' })`
  margin: 0;
  font-weight: ${({ bold }) => (bold ? '700' : '500')};
  color: ${({ theme }) => theme.colorNeutralNeutral1};
  text-overflow: ellipsis;
  overflow: hidden;
`;

interface SidebarListItemProps {
  isHighlighted?: boolean;
}

export const SidebarListSubHeading = styled(Paragraph).attrs({ typography: 'ParagraphTinyHeavy' })`
  margin: 0;
  min-height: 1em;
  color: ${({ theme }) => theme.colorNeutralNeutral3};
`;

export const SidebarListItem = styled.li<SidebarListItemProps>`
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  background: ${({ theme, isHighlighted }) => (isHighlighted ? theme.colorStatusInfoBg : theme.colorBackgroundBrightWhite)};
  border: 1px solid ${({ theme }) => theme.colorNeutralNeutral9};
  border-radius: 0.375rem;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colorNeutralNeutral7};
  }

  ${SidebarListSubHeading} {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    text-transform: capitalize;

    svg {
      width: 0.75rem;
      height: 0.75rem;
    }
  }
`;

export { MuiModal };
